import React from 'react';

const SOURCE_META = {
  department: {
    label: 'Department',
    dark: 'border-blue-800/80 bg-blue-950/50 text-blue-200',
    light: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  team: {
    label: 'Team',
    dark: 'border-violet-800/80 bg-violet-950/50 text-violet-200',
    light: 'border-violet-200 bg-violet-50 text-violet-700',
  },
  crew: {
    label: 'Crew',
    dark: 'border-cyan-800/80 bg-cyan-950/50 text-cyan-200',
    light: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  },
  user: {
    label: 'Direct',
    dark: 'border-slate-600 bg-slate-800 text-slate-300',
    light: 'border-slate-200 bg-slate-50 text-slate-600',
  },
};

export const normalizeScenarioCandidateSources = (value = {}) => {
  const raw = Array.isArray(value)
    ? value
    : Array.isArray(value?.candidate_sources)
      ? value.candidate_sources
      : value?.source_type && value?.source_id
        ? [{ type: value.source_type, id: value.source_id, name: value.source_name }]
        : [];
  return raw.reduce((sources, source) => {
    const type = String(source?.type || '').trim().toLowerCase();
    const id = String(source?.id || '').trim();
    const name = String(source?.name || id).trim();
    const key = `${type}:${id}`;
    if (!SOURCE_META[type] || !id || !name || sources.some((entry) => entry.key === key)) return sources;
    sources.push({ key, type, id, name });
    return sources;
  }, []);
};

export const scenarioCandidateSourceSearchText = (value) => normalizeScenarioCandidateSources(value)
  .flatMap((source) => [source.type, SOURCE_META[source.type]?.label, source.name])
  .join(' ')
  .toLowerCase();

export const scenarioCandidateReason = (value, role = 'this role') => {
  const sources = normalizeScenarioCandidateSources(value);
  if (!sources.length) return `Saved as a default candidate for ${role}.`;
  if (sources.length > 1) return `Matches ${sources.length} candidate sources selected for ${role}.`;
  const source = sources[0];
  if (source.type === 'user') return `Selected directly as a default candidate for ${role}.`;
  return `Member of the selected ${SOURCE_META[source.type].label.toLowerCase()} for ${role}.`;
};

export const scenarioCandidateVia = (value) => {
  const source = normalizeScenarioCandidateSources(value)[0];
  return source ? { type: source.type, id: source.id, name: source.name } : null;
};

export default function ScenarioCandidateSourceChips({ value, dark = true, className = '' }) {
  // Direct-user selection is implicit in the person's own row. Keep only the
  // Scenario Builder group provenance that explains why that person matched.
  const sources = normalizeScenarioCandidateSources(value).filter((source) => source.type !== 'user');
  if (!sources.length) return null;
  return (
    <span className={`inline-flex min-w-0 flex-wrap gap-1 ${className}`}>
      {sources.map((source) => {
        const meta = SOURCE_META[source.type];
        return (
          <span
            key={source.key}
            className={`inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] ${dark ? meta.dark : meta.light}`}
            title="Selected as a default candidate in Scenario Builder"
          >
            <span className="font-extrabold uppercase tracking-wide">{meta.label}</span>
            <span className="truncate font-semibold">{source.name}</span>
          </span>
        );
      })}
    </span>
  );
}
